import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { API_URL, ENVIRONMENT } from '../../config';

export interface StreamingMetricDTO {
  id: string;
  timestamp: number;
  provider: string;
  model: string;
  interviewType: string;
  promptId: string;
  userId: string;
  userName: string;
  question: string;
  ttfbMs: number;
  firstTokenMs: number;
  totalDurationMs: number;
  status: string;
  errorCode?: string;
  region?: string;
  isFallback: boolean;
  environment: string;
  tokenCount?: number;
}

@Injectable({
  providedIn: 'root'
})
export class MetricsService implements OnDestroy {

  private buffer: StreamingMetricDTO[] = [];
  private flushTimeout: any;
  private readonly FLUSH_DELAY_MS = 2000; // flush 2s after last record
  private readonly endpoint = `${API_URL}/streaming-metrics/batch`;

  constructor(private http: HttpClient) {
    // Flush on page unload
    if (typeof window !== 'undefined') {
      window.addEventListener('beforeunload', this.onBeforeUnload);
    }
  }

  ngOnDestroy(): void {
    if (this.flushTimeout) {
      clearTimeout(this.flushTimeout);
    }
    if (typeof window !== 'undefined') {
      window.removeEventListener('beforeunload', this.onBeforeUnload);
    }
    this.flush();
  }

  record(metric: Partial<StreamingMetricDTO>): void {
    const fullMetric: StreamingMetricDTO = {
      id: this.generateUUID(),
      timestamp: Date.now(),
      provider: metric.provider || 'unknown',
      model: metric.model || 'unknown',
      interviewType: metric.interviewType || '',
      promptId: metric.promptId || '',
      userId: this.getUserId(),
      userName: this.getUserName(),
      question: metric.question || '',
      ttfbMs: metric.ttfbMs || 0,
      firstTokenMs: metric.firstTokenMs || 0,
      totalDurationMs: metric.totalDurationMs || 0,
      status: metric.status || 'unknown',
      errorCode: metric.errorCode,
      region: metric.region,
      isFallback: metric.isFallback || false,
      environment: ENVIRONMENT,
      tokenCount: metric.tokenCount,
    };

    console.log('[METRICS]', {
      provider: fullMetric.provider,
      model: fullMetric.model,
      ttfbMs: fullMetric.ttfbMs,
      firstTokenMs: fullMetric.firstTokenMs,
      totalDurationMs: fullMetric.totalDurationMs,
      status: fullMetric.status,
      interviewType: fullMetric.interviewType,
      promptId: fullMetric.promptId,
    });

    this.buffer.push(fullMetric);
    this.scheduleFlush();
  }

  private scheduleFlush(): void {
    if (this.flushTimeout) {
      clearTimeout(this.flushTimeout);
    }
    this.flushTimeout = setTimeout(() => this.flush(), this.FLUSH_DELAY_MS);
  }

  flush(): void {
    if (this.flushTimeout) {
      clearTimeout(this.flushTimeout);
      this.flushTimeout = null;
    }
    if (this.buffer.length === 0) return;

    const batch = [...this.buffer];
    this.buffer = [];

    this.http.post(this.endpoint, batch).subscribe({
      next: () => {
        // Successfully flushed
      },
      error: (err) => {
        // Silently ignore - don't break user experience
        console.debug('[METRICS] Flush failed, metrics will be lost:', err.status);
      }
    });
  }

  private onBeforeUnload = (): void => {
    if (this.buffer.length === 0) return;
    // Use sendBeacon for reliable delivery on page unload
    try {
      const blob = new Blob([JSON.stringify(this.buffer)], { type: 'application/json' });
      navigator.sendBeacon(this.endpoint, blob);
      this.buffer = [];
    } catch {
      // Fallback: try sync XHR (last resort)
    }
  };

  private getUserId(): string {
    try {
      return sessionStorage.getItem('userId') || '';
    } catch { }
    return '';
  }

  private getUserName(): string {
    try {
      const details = sessionStorage.getItem('userDetails');
      if (details) {
        const parsed = JSON.parse(details);
        return parsed.firstName || parsed.userName || parsed.email || '';
      }
      return sessionStorage.getItem('email') || '';
    } catch { }
    return '';
  }

  private generateUUID(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }
}
